import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Minus, Pencil, Trash2, Search, PackageX, PackagePlus, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  type: "piece", condition: "neuf", reference: "", designation: "", marque: "",
  dimension: "", quantite: 0, seuil_alerte: 2, prix_achat: 0, prix_vente: 0, emplacement: "",
};

export default function Stock() {
  const { isFull } = useAuth();
  const [items, setItems] = useState([]);
  const [fType, setFType] = useState("all");
  const [fCond, setFCond] = useState("all");
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjItem, setAdjItem] = useState(null);
  const [adjQty, setAdjQty] = useState(0);

  const load = useCallback(async () => {
    const params = {};
    if (fType !== "all") params.type = fType;
    if (fCond !== "all") params.condition = fCond;
    const { data } = await api.get("/stock", { params });
    setItems(data);
  }, [fType, fCond]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((i) => {
    const low = i.quantite <= i.seuil_alerte;
    if (onlyLow && !low) return false;
    if (q && !`${i.designation} ${i.reference} ${i.marque} ${i.dimension}`.toLowerCase().includes(q.toLowerCase()))
      return false;
    return true;
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (i) => {
    setForm({ ...EMPTY, ...i }); setEditId(i.id); setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      quantite: Number(form.quantite), seuil_alerte: Number(form.seuil_alerte),
      prix_achat: Number(form.prix_achat), prix_vente: Number(form.prix_vente),
    };
    try {
      if (editId) await api.put(`/stock/${editId}`, payload);
      else await api.post("/stock", payload);
      toast.success(editId ? "Article mis à jour" : "Article ajouté");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdjust = (i) => { setAdjItem(i); setAdjQty(i.quantite); setAdjOpen(true); };
  const saveAdjust = async () => {
    const target = Math.max(0, Number(adjQty) || 0);
    const delta = target - adjItem.quantite;
    if (delta !== 0) {
      await api.patch(`/stock/${adjItem.id}/adjust`, { delta });
      toast.success(`Quantité mise à jour : ${target}`);
    }
    setAdjOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/stock/${id}`);
    toast.success("Article supprimé");
    load();
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="stock-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC]">Stock & Pneus</h1>
          <p className="text-[#94A3B8] text-sm">Pièces et pneus neufs & d'occasion.</p>
        </div>
        <Button data-testid="add-stock-button" onClick={openNew} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <PackagePlus className="w-4 h-4 mr-2" /> Nouvel article
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input data-testid="stock-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…" className="pl-9 w-56 bg-[#182232] border-[#2A364F] text-[#F8FAFC]" />
        </div>
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger data-testid="filter-type" className="w-40 bg-[#182232] border-[#2A364F] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value="piece">Pièces</SelectItem>
            <SelectItem value="pneu">Pneus</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fCond} onValueChange={setFCond}>
          <SelectTrigger data-testid="filter-condition" className="w-40 bg-[#182232] border-[#2A364F] text-[#F8FAFC]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Neuf & Occasion</SelectItem>
            <SelectItem value="neuf">Neuf</SelectItem>
            <SelectItem value="occasion">Occasion</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="filter-low-toggle" variant={onlyLow ? "default" : "outline"}
          onClick={() => setOnlyLow(!onlyLow)}
          className={onlyLow ? "bg-[#EF4444] hover:bg-[#DC2626] text-white" : "border-[#2A364F] text-[#94A3B8] bg-transparent"}>
          <PackageX className="w-4 h-4 mr-2" /> Stock bas
        </Button>
      </div>

      <div className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead>
              <tr className="text-[#64748B] text-xs uppercase tracking-wider border-b border-[#2A364F]">
                <th className="text-left px-4 py-3">Désignation</th>
                <th className="text-left px-4 py-3">Type / État</th>
                <th className="text-left px-4 py-3">Réf.</th>
                <th className="text-right px-4 py-3">Prix vente TTC</th>
                <th className="text-center px-4 py-3">Quantité</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A364F]">
              {filtered.map((i) => {
                const low = i.quantite <= i.seuil_alerte;
                return (
                  <tr key={i.id} data-testid={`stock-row-${i.id}`} className={`hover:bg-[#1E2A3E] ${low ? "bg-[#EF4444]/5" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="text-[#F8FAFC] font-medium">{i.designation}</div>
                      <div className="text-xs text-[#64748B]">{i.marque} {i.dimension && `· ${i.dimension}`} {i.emplacement && `· 📍${i.emplacement}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs text-[#94A3B8]">{i.type === "pneu" ? "Pneu" : "Pièce"}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded border w-fit ${i.condition === "neuf" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}`}>
                          {i.condition === "neuf" ? "Neuf" : "Occasion"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="font-mono text-xs text-cyan-400">{i.reference || "—"}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-[#F8FAFC]">{Number(i.prix_vente).toFixed(2)} €</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-3">
                        <span className={`font-mono font-bold w-8 text-center ${low ? "text-[#EF4444]" : "text-[#F8FAFC]"}`}>{i.quantite}</span>
                        <Button data-testid={`adjust-stock-${i.id}`} size="sm" variant="outline" onClick={() => openAdjust(i)} className="h-8 border-[#2A364F] bg-transparent text-[#94A3B8] hover:text-white">
                          <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Ajuster
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`edit-stock-${i.id}`} onClick={() => openEdit(i)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                        {isFull && <button data-testid={`delete-stock-${i.id}`} onClick={() => del(i.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#64748B]">Aucun article.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Ajuster la quantité</DialogTitle></DialogHeader>
          {adjItem && (
            <div className="space-y-3">
              <div className="text-sm text-[#F8FAFC]">{adjItem.designation} <span className="text-xs text-[#64748B] font-mono">{adjItem.reference}</span></div>
              <div className="text-xs text-[#64748B]">Quantité actuelle : <span className="font-mono text-[#F8FAFC]">{adjItem.quantite}</span></div>
              <div className="flex items-center justify-center gap-3 pt-1">
                <button data-testid="adjust-decr" onClick={() => setAdjQty((v) => Math.max(0, (Number(v) || 0) - 1))} className="w-11 h-11 rounded-lg bg-[#0A0D14] border border-[#2A364F] text-[#94A3B8] hover:text-white flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <Input data-testid="adjust-qty-input" type="number" min={0} value={adjQty} onChange={(e) => setAdjQty(e.target.value)} className="w-24 text-center font-mono text-lg bg-[#182232] border-[#2A364F]" />
                <button data-testid="adjust-incr" onClick={() => setAdjQty((v) => (Number(v) || 0) + 1)} className="w-11 h-11 rounded-lg bg-[#0A0D14] border border-[#2A364F] text-[#94A3B8] hover:text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-adjust-button" onClick={saveAdjust} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC] max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[#94A3B8]">Désignation *</Label>
              <Input data-testid="stock-designation-input" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="stock-type-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="piece">Pièce</SelectItem><SelectItem value="pneu">Pneu</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#94A3B8]">État</Label>
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="stock-condition-select" className="mt-1 bg-[#182232] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="neuf">Neuf</SelectItem><SelectItem value="occasion">Occasion</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-[#94A3B8]">Référence</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Marque</Label><Input value={form.marque} onChange={(e) => setForm({ ...form, marque: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            {form.type === "pneu" && (
              <div className="col-span-2"><Label className="text-[#94A3B8]">Dimension (ex: 205/55 R16)</Label><Input value={form.dimension} onChange={(e) => setForm({ ...form, dimension: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            )}
            <div><Label className="text-[#94A3B8]">Quantité</Label><Input data-testid="stock-quantite-input" type="number" value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Seuil d'alerte</Label><Input type="number" value={form.seuil_alerte} onChange={(e) => setForm({ ...form, seuil_alerte: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Prix d'achat HT (€)</Label><Input type="number" step="0.01" value={form.prix_achat} onChange={(e) => setForm({ ...form, prix_achat: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div><Label className="text-[#94A3B8]">Prix de vente TTC (€)</Label><Input type="number" step="0.01" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" /></div>
            <div className="col-span-2"><Label className="text-[#94A3B8]">Emplacement</Label><Input value={form.emplacement} onChange={(e) => setForm({ ...form, emplacement: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Étagère A3…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-stock-button" onClick={save} disabled={!form.designation} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
